"use client";

import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

export interface CheckboxProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    "style"
  > {
  label?: string;
  description?: string;
  error?: string;
  indeterminate?: boolean;
  /** @deprecated rsuite compat — ignored */
  value?: string;
  className?: string;
  /** @deprecated rsuite compat — maps to onCheckedChange */
  onRsuiteChange?: (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ label, description, error, indeterminate, id, value: _value, className, onRsuiteChange, onCheckedChange, ...props }, ref) => {
  const handleCheckedChange = (checked: CheckboxPrimitive.CheckedState) => {
    onCheckedChange?.(checked);
    onRsuiteChange?.(checked, checked === true, {} as React.ChangeEvent<HTMLInputElement>);
  };
  const generatedId = React.useId();
  const checkboxId = id ?? generatedId;

  return (
    <div className="flex items-start gap-3">
      <CheckboxPrimitive.Root
        ref={ref}
        id={checkboxId}
        className={cn(
          "peer mt-0.5 h-5 w-5 shrink-0 rounded-md border-2 bg-white/70 transition-all duration-200 outline-none dark:bg-zinc-950/70",
          className,
          "focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-1",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:shadow-[0_0_0_3px_rgba(99,102,241,0.14)]",
          "data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:shadow-[0_0_0_3px_rgba(99,102,241,0.14)]",
          error
            ? "border-destructive data-[state=checked]:border-destructive data-[state=checked]:bg-destructive"
            : "border-input hover:border-primary/70"
        )}
        onCheckedChange={handleCheckedChange}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-primary-foreground">
          {indeterminate ? (
            <Minus className="h-3 w-3" strokeWidth={3} />
          ) : (
            <Check className="h-3 w-3" strokeWidth={3} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>

      {(label ?? description ?? error) && (
        <div className="flex flex-col gap-0.5">
          {label && (
            <label
              htmlFor={checkboxId}
              className="cursor-pointer select-none text-sm font-medium text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {label}
            </label>
          )}
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}
    </div>
  );
});

Checkbox.displayName = "Checkbox";

export { Checkbox };
